/**
 * useMaterialReadiness — convenience hook (Phase 3).
 *
 * Pulls inputs from canonical contexts and feeds the pure resolver.
 * UI surfaces MUST consume only this hook (or the resolver directly with
 * explicit inputs) — no inline material readiness interpretation.
 */
import { useMemo } from "react";
import type { Job } from "@/data/types";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { getJobBomReconciliation } from "@/data/materialsReconciliation";
import { useFieldMaterialFeedback } from "@/data/procurement/useFieldMaterialFeedback";
import { useSubstitutionRequests } from "@/data/procurement/useSubstitutionRequests";
import {
  resolveMaterialReadiness,
  type MaterialReadinessVerdict,
} from "@/data/procurement/materialReadinessResolver";

export function useMaterialReadiness(job: Job | null | undefined): MaterialReadinessVerdict {
  const { getPOsByJob, purchaseOrders: allPurchaseOrders } = useVendorPO();
  const { receivingRecords, jobAllocations } = useWarehouseInventory();
  const { packets } = useOrderingPacket();
  const { drafts } = useOrderDraft();
  const fieldFeedback = useFieldMaterialFeedback(job?.id);
  const substitutionSummary = useSubstitutionRequests(job?.id);

  return useMemo(() => {
    if (!job) return resolveMaterialReadiness({ job: null });
    const draft = drafts[job.id];
    const purchaseOrders = draft?.status === "pushed" && draft.pushedPoIds?.length
      ? allPurchaseOrders.filter((po) => draft.pushedPoIds?.includes(po.poId))
      : getPOsByJob(job.id);
    const orderingPackets = packets
      .filter((p) => p.jobId === job.id)
      .flatMap((p) => p.lineItems);
    const jobReceiving = receivingRecords.filter((r) => r.jobId === job.id);
    const jobAllocs = jobAllocations.filter((a) => a.jobId === job.id);
    const reconciliation = getJobBomReconciliation(job);
    return resolveMaterialReadiness({
      job,
      purchaseOrders,
      orderingPackets,
      receivingRecords: jobReceiving,
      jobAllocations: jobAllocs,
      reconciliation,
    });
  }, [job, drafts, allPurchaseOrders, getPOsByJob, receivingRecords, jobAllocations, packets]);
}
